// pageName → MODULE_KEYS entry. Required for module gating in Sidebar.jsx.
// See docs/decisions/0003-module-gating-semantics.md for the contract.
//
// Several pageNames map to the same module on purpose — these are the
// fusions planned for PR4 (Facturas+TicketBAI+VeriFactu, Gastos+OCR,
// Bancos+Conciliación, Empleados+Organigrama+Skills, etc.). Once those
// fusions land, the redundant entries can be dropped along with the pages.
//
// IMPORTANT: every pageName referenced from Layout.jsx must have an entry
// here OR it will be hidden whenever a tenant has a customized
// `theme.modules`. The test moduleMapping.test.js enforces full coverage.

export const PAGE_TO_MODULE = {
  // Inicio
  Home: 'dashboard',

  // Finanzas
  Facturas:        'invoices',
  Gastos:          'expenses',
  GastosEscaneos:  'expenses',     // OCR fusionado en Gastos en PR4
  BandejaDocumentos: 'expenses',   // inbox OCR transversal (gastos + facturas)
  Bancos:          'bank',
  Conciliacion:    'bank',         // fusionado en Bancos en PR4
  BancoCuenta:     'bank',
  Informes:        'fin_reports',
  Impuestos:       'taxes',
  ImpuestosHub:    'taxes',         // sidebar entry — Modelos AEAT + Centro AEAT
  AEATHub:         'taxes',         // fusionado en Impuestos en PR4-bis #10
  TicketBAIStatus: 'invoices',     // fusionado en Facturas en PR4
  VeriFactu:       'invoices',     // fusionado en Facturas en PR4
  Accounting:      'accounting', // hub-con-sidebar — único módulo Conta desde PR14 (legacy retirada)
  Cartera:         'accounting', // Cartera de unidades de ingreso recurrente (Fase D)
  // `DataImport` (Centro de Datos) ya NO se gatea por módulo: ver ALWAYS_ON_PAGES.

  // Comercial / contactos
  Clientes:              'customers',
  Contactos:             'customers',
  Proveedores:           'suppliers',
  CRMSegmentacion:       'segmentation',
  CRMPipeline:           'pipeline',
  CRMSecuencias:         'sequences',
  CRMActividades:        'activities',

  // Inventario / suppliers
  Inventario:               'inventory',  // PR4-bis #6 fused page (sidebar entry)
  Productos:                'inventory',
  InventarioStock:          'inventory',
  InventarioMovimientos:    'inventory',
  InventarioPedidos:        'inventory',

  // RRHH / Personas
  PortalEmpleado:    'my_zone',
  SupervisorPanel:   'team',
  RRHHEmpleados:     'team',
  InformesRRHH:      'team',
  Organigrama:       'team',
  SkillsManagement:  'team',
  TimesheetManager:  'time',
  Onboarding:        'lifecycle',
  ContratosRRHH:     'lifecycle',
  HRCommunications:  'lifecycle',
  Nominas:           'comp',
  DietasGastos:      'comp',
  SalarySimulator:   'comp',

  // Operaciones / proyectos
  Projects:                 'projects_list',
  ProjectDetail:            'projects_list',
  MyTasks:                  'my_tasks',
  Recursos:                 'resources',
  GlobalKanban:             'projects_list',
  ProjectBillingOverview:   'profitability',
  ProyectoInversion:        'profitability',
  RentabilidadProyectos:    'profitability',  // sidebar entry for the fused view

  // Inteligencia
  AsistenteAgentic: 'ai_assistant',
  AriaActividad:    'ai_assistant',  // sidebar entry — feed "Actividad de ARIA"
  Insights:         'insights',     // sidebar entry — BI + Analytics + Resumen
  BIDashboard:      'insights',     // fusionado en Insights en PR4-bis #10
  Analytics:        'insights',     // fusionado en Insights en PR4-bis #10
  ExecutiveSummary: 'insights',     // fusionado en Insights en PR4-bis #10
  IESEPanel:        'insights',     // DevOps deep-link only, but keep gateable

  // Sistema
  Configuracion:    'settings',
  // Vive en el menú de Sistema pero su módulo es `taxes`, no `settings`: la
  // pantalla existe por la fiscalidad —certificado FNMT, canal VeriFACTU/
  // TicketBAI—, así que un tenant sin el módulo fiscal no debe verla, y uno que
  // recorte `settings` sí debe conservarla. Mismo criterio que `AEATHub`.
  ConfiguracionFiscal: 'taxes',
  // Sprint K3 follow-up (#730) — sub-page de Sistema.
  BankAccountsMapping: 'settings',
  RolesManagement:  'roles',
  UserManual:       'docs',
  ManualRRHH:       'docs',
  AWSDocumentation: 'docs',
  AWSMigrationGuide:'docs',
  InfrastructureDocs:'docs',
};

// Pages that the Sidebar must NEVER hide regardless of theme.modules.
// Rationale: lockout protection (see ADR-0003).
//
// PortalAsesor (FASE 5 Sub-PR 5.2) entra aquí porque su gate es role +
// permission (vía useCanSeePortalAsesor), NO theme.modules. Añadir un
// MODULE_KEY nuevo `asesor` requeriría coordinar con el enum Zod en
// backend/src/utils/schemas.ts (companyThemeSchema) — out of scope FE-only.
// (ApprovalsDashboard salió del menú: ahora vive dentro del Centro de Control
// ARIA del Home, no en el sidebar → ya no necesita estar aquí.)
// `PrimerosPasosPage` va aquí y no en PAGE_TO_MODULE a propósito: ocultar el
// checklist de configuración inicial porque el usuario apagó un pilar del menú
// sería dejarle sin la pantalla que precisamente sirve para terminar de montar
// la empresa. Su visibilidad la decide el onboarding (`showIf: !onboardingDone`
// en Layout.jsx), no la personalización de módulos.
/**
 * Páginas visibles con cualquier plan y cualquier `theme.modules`.
 *
 * `DataImport` (Centro de Datos, `/datos`) entró aquí porque estaba mapeado al
 * módulo `accounting`, que no existe en el plan starter — el que tiene por
 * defecto toda empresa nueva. Resultado: los nueve importadores, incluido el de
 * empleados, eran invisibles justo para quien acaba de registrarse y tiene que
 * meter sus datos. Es una superficie de puesta en marcha, de la misma familia
 * que `PrimerosPasosPage`, y no de contabilidad: importar el maestro de
 * trabajadores no es contabilizar nada.
 *
 * Quién puede usarlo lo sigue decidiendo el permiso `accounting.import` en la
 * ruta (`features/platform/routes.jsx`), que no ha cambiado.
 *
 * `Contactos` entra por el mismo motivo que `DataImport`: es la agenda MAESTRA
 * —clientes y proveedores, cada uno en su pestaña— de la que dependen facturas,
 * gastos y cobros, no un área de negocio opcional. Colgaba del módulo `customers`
 * y este del pilar `crm`, así que un autónomo (perfil → `['finance','ai']`) salía
 * del embudo con Facturas visible y sin nadie a quien facturar.
 *
 * Va AQUÍ además de en `ALWAYS_ON_MODULES` (components/plan/setupProfiles.js)
 * porque aquello solo arregla las escrituras futuras: las empresas ya creadas
 * tienen su `settings.theme.modules` persistido SIN `customers`, y sin esta
 * entrada seguirían sin ver Contactos hasta volver a pasar por el asistente.
 * Defender en el render evita una migración de datos.
 *
 * `ConfiguracionFiscal` es superficie de puesta en marcha, no de fiscalidad
 * avanzada: la `ReadyScreen` del embudo recomienda "Certificado digital" y lleva
 * ahí, pero la página cuelga del módulo `taxes` — quien apagaba el pilar
 * `finance` recibía la recomendación hacia una página fuera de su menú.
 *
 * `Clientes` y `Proveedores` NO están: existen en `PAGE_TO_MODULE` pero no son
 * entradas del sidebar (se llega a ellas como pestañas de `Contactos`), así que
 * el filtro nunca las ve. Añadirlas sería configuración muerta — el test
 * `always-on pages are present in the navigation` lo rechaza, y hace bien.
 *
 * `Soporte` va aquí y no en `PAGE_TO_MODULE` porque no es una función del
 * producto que se contrate: el botón de reportar incidencias lo tiene todo el
 * mundo en la barra superior, así que la pantalla donde se leen las respuestas
 * tiene que estar donde esté ese botón. Colgarla de un módulo dejaría a un
 * cliente pudiendo reportar y sin poder leer lo que le contestamos.
 */
export const ALWAYS_ON_PAGES = new Set([
  'Home', 'Configuracion', 'PortalAsesor', 'PrimerosPasosPage', 'DataImport',
  'Contactos', 'ConfiguracionFiscal', 'Soporte',
]);

/**
 * Returns the module key for a given page, or null if the page isn't
 * gateable (legacy / not yet mapped). Pages without a mapping are treated
 * as visible to avoid silent disappearance — the unit test forces coverage
 * for every page in the navigation tree.
 */
export function moduleForPage(pageName) {
  return PAGE_TO_MODULE[pageName] ?? null;
}
